import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — react-icons + React 19 typings
import {
  FaBook as FaBookRaw,
  FaChevronDown as FaChevronDownRaw,
  FaChevronUp as FaChevronUpRaw,
  FaClone as FaCloneRaw,
  FaEdit as FaEditRaw,
  FaEye as FaEyeRaw,
  FaFilter as FaFilterRaw,
  FaFlask as FaFlaskRaw,
  FaGraduationCap as FaGraduationCapRaw,
  FaPlay as FaPlayRaw,
  FaQuestion as FaQuestionRaw,
  FaSearch as FaSearchRaw,
  FaTrashAlt as FaTrashAltRaw,
} from 'react-icons/fa';
import { teacherService, type TeacherLesson } from '../../services/classroom/teacherService';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaBook: any = FaBookRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaChevronDown: any = FaChevronDownRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaChevronUp: any = FaChevronUpRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaClone: any = FaCloneRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaEdit: any = FaEditRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaEye: any = FaEyeRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaFilter: any = FaFilterRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaFlask: any = FaFlaskRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaGraduationCap: any = FaGraduationCapRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaPlay: any = FaPlayRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaQuestion: any = FaQuestionRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaSearch: any = FaSearchRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaTrashAlt: any = FaTrashAltRaw;

const MOBILE_BREAKPOINT = '960px';

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const PageHeaderRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1.25rem;
`;

const TitleBlock = styled.div`
  min-width: 0;
`;

const PageTitle = styled.h2`
  margin: 0 0 0.35rem;
  font-family: 'Poppins', sans-serif;
  font-size: clamp(1.35rem, 2.2vw, 1.65rem);
  font-weight: 700;
  color: var(--text);
`;

const PageSubtitle = styled.p`
  margin: 0;
  font-size: 0.95rem;
  color: var(--textSecondary, var(--onSurfaceVariant));
  max-width: 36rem;
  line-height: 1.45;
`;

const StatsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

const StatCard = styled.div<{ $accent: 'primary' | 'warning' | 'muted' }>`
  min-width: 118px;
  padding: 0.75rem 1rem 0.75rem 0.85rem;
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 12px;
  border-left-width: 4px;
  border-left-color: ${({ $accent }) =>
    $accent === 'primary'
      ? 'var(--primary)'
      : $accent === 'warning'
        ? 'var(--warning, #e8a23c)'
        : 'var(--outline)'};
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
`;

const StatLabel = styled.div`
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: var(--textSecondary, var(--onSurfaceVariant));
`;

const StatValue = styled.div`
  margin-top: 0.25rem;
  font-family: 'Poppins', sans-serif;
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text);
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.65rem;
  padding: 0.85rem 1rem;
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 14px;
`;

const SearchWrap = styled.div`
  flex: 1;
  min-width: 200px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.85rem;
  background: var(--surfaceVariant);
  border: 1px solid var(--outline);
  border-radius: 10px;
  color: var(--textSecondary, var(--onSurfaceVariant));
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  font-size: 0.9rem;
  font-family: 'Open Sans', sans-serif;
  color: var(--text);
  outline: none;
  &::placeholder {
    color: var(--textSecondary, var(--onSurfaceVariant));
  }
`;

const Select = styled.select`
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  font-family: 'Open Sans', sans-serif;
  border-radius: 10px;
  border: 1px solid var(--outline);
  background: var(--surface);
  color: var(--text);
  min-width: 150px;
  cursor: pointer;
`;

const FilterBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 0.85rem;
  font-size: 0.875rem;
  font-weight: 600;
  font-family: 'Open Sans', sans-serif;
  border-radius: 10px;
  border: 1px solid var(--outline);
  background: var(--surface);
  color: var(--text);
  cursor: pointer;
  &:hover {
    border-color: var(--primary);
    color: var(--primary);
  }
`;

const PrimaryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.55rem 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  font-family: 'Open Sans', sans-serif;
  border-radius: 10px;
  border: none;
  background: var(--primary);
  color: var(--onPrimary);
  cursor: pointer;
  white-space: nowrap;
  &:hover {
    background: var(--primaryDark);
  }
`;

const FiltersPanel = styled.div<{ $open: boolean }>`
  display: ${({ $open }) => ($open ? 'flex' : 'none')};
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: flex-end;
  padding: 0.75rem 1rem 1rem;
  margin-top: -0.65rem;
  background: var(--surfaceVariant);
  border: 1px solid var(--outline);
  border-top: none;
  border-radius: 0 0 14px 14px;
`;

const FilterField = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--textSecondary, var(--onSurfaceVariant));
  input {
    padding: 0.45rem 0.6rem;
    border-radius: 8px;
    border: 1px solid var(--outline);
    font-family: 'Open Sans', sans-serif;
    font-size: 0.875rem;
  }
`;

const ModuleBlock = styled.section`
  margin-top: 0.25rem;
`;

const ModuleHead = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;
  margin-bottom: 0.65rem;
`;

const ModuleIconBox = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: var(--primaryContainer, var(--surfaceVariant));
  color: var(--primary);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const ModuleTitles = styled.div`
  min-width: 0;
`;

const ModuleTitle = styled.h3`
  margin: 0 0 0.2rem;
  font-family: 'Poppins', sans-serif;
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--text);
`;

const ModuleMeta = styled.p`
  margin: 0;
  font-size: 0.85rem;
  color: var(--textSecondary, var(--onSurfaceVariant));
`;

const LessonRow = styled.div`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto auto auto;
  gap: 0.75rem 1rem;
  align-items: center;
  padding: 0.85rem 1rem;
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 12px;
  margin-bottom: 0.5rem;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    grid-template-columns: auto 1fr;
    grid-template-rows: auto auto;
  }
`;

const TypeIcon = styled.div<{ $t: 'video' | 'reading' | 'quiz' }>`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${({ $t }) =>
    $t === 'video'
      ? 'var(--primaryContainer, var(--surfaceVariant))'
      : $t === 'quiz'
        ? 'var(--tertiaryContainer, var(--surfaceVariant))'
        : 'var(--surfaceVariant)'};
  color: var(--primary);
`;

const LessonInfo = styled.div`
  min-width: 0;
`;

const LessonTitle = styled.div`
  font-weight: 700;
  font-family: 'Poppins', sans-serif;
  font-size: 0.95rem;
  color: var(--text);
`;

const LessonSub = styled.div`
  margin-top: 0.2rem;
  font-size: 0.8rem;
  color: var(--textSecondary, var(--onSurfaceVariant));
`;

const StatusPill = styled.span<{ $status: 'published' | 'scheduled' | 'draft' }>`
  font-size: 0.65rem;
  font-weight: 800;
  letter-spacing: 0.05em;
  padding: 0.3rem 0.55rem;
  border-radius: 999px;
  white-space: nowrap;
  ${({ $status }) =>
    $status === 'published'
      ? `
    background: var(--successContainer, rgba(34, 160, 107, 0.15));
    color: var(--success, #1a8f5c);
  `
      : $status === 'scheduled'
        ? `
    background: var(--primaryContainer, var(--surfaceVariant));
    color: var(--primary);
  `
        : `
    background: var(--surfaceVariant);
    color: var(--textSecondary, var(--onSurfaceVariant));
  `}
`;

const EngagementBlock = styled.div`
  min-width: 100px;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    min-width: 0;
  }
`;

const EngagementLabel = styled.div`
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: var(--textSecondary, var(--onSurfaceVariant));
  margin-bottom: 0.25rem;
`;

const EngBarTrack = styled.div`
  height: 4px;
  border-radius: 999px;
  background: var(--surfaceVariant);
  overflow: hidden;
`;

const EngBarFill = styled.div<{ $pct: number }>`
  height: 100%;
  width: ${({ $pct }) => Math.min(100, Math.max(0, $pct))}%;
  border-radius: 999px;
  background: var(--primary);
`;

const EngPct = styled.div`
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--text);
  margin-top: 0.2rem;
`;

const IconBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: var(--textSecondary, var(--onSurfaceVariant));
  cursor: pointer;
  &:hover {
    background: var(--surfaceVariant);
    color: var(--primary);
  }
  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
`;

const ActionCluster = styled.div`
  display: flex;
  align-items: center;
  gap: 0.15rem;
  flex-shrink: 0;
`;

const LessonStatusCell = styled.div`
  justify-self: end;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    grid-column: 1 / -1;
    justify-self: start;
  }
`;

const LessonEngageCell = styled(EngagementBlock)`
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    grid-column: 1 / -1;
  }
`;

const LessonActionsCell = styled(ActionCluster)`
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    grid-column: 1 / -1;
    justify-content: flex-end;
  }
`;

const LessonPreviewPanel = styled.div`
  margin: -0.35rem 0 0.5rem;
  padding: 0.75rem 1rem;
  background: var(--surfaceVariant);
  border: 1px solid var(--outline);
  border-radius: 0 0 12px 12px;
  font-size: 0.85rem;
  line-height: 1.5;
  color: var(--textSecondary, var(--onSurfaceVariant));
  border-top: none;
`;

const EditorCard = styled.article`
  padding: 1.15rem 1.25rem;
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 16px;
`;

const EditorTitle = styled.h3`
  margin: 0 0 0.75rem;
  font-family: 'Poppins', sans-serif;
  font-size: 1.05rem;
`;

const EditorGrid = styled.div`
  display: grid;
  gap: 0.65rem;
  label {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    font-size: 0.8rem;
    font-weight: 600;
    color: var(--textSecondary, var(--onSurfaceVariant));
  }
  input,
  textarea,
  select {
    padding: 0.5rem 0.65rem;
    border-radius: 8px;
    border: 1px solid var(--outline);
    font-family: 'Open Sans', sans-serif;
    font-size: 0.9rem;
    color: var(--text);
    background: var(--surface);
  }
`;

const EditorActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const GhostBtn = styled.button`
  padding: 0.5rem 0.85rem;
  font-size: 0.875rem;
  font-weight: 600;
  font-family: 'Open Sans', sans-serif;
  border-radius: 10px;
  border: 1px solid var(--outline);
  background: var(--surface);
  color: var(--text);
  cursor: pointer;
  &:hover {
    border-color: var(--primary);
    color: var(--primary);
  }
`;

function startOfDay(d: Date): number {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x.getTime();
}

function lessonStatus(lesson: TeacherLesson): 'published' | 'scheduled' | 'draft' {
  if (!lesson.lesson_date) return 'draft';
  const t = new Date(lesson.lesson_date).getTime();
  if (Number.isNaN(t)) return 'draft';
  const today = startOfDay(new Date());
  const day = startOfDay(new Date(lesson.lesson_date));
  if (day > today) return 'scheduled';
  return 'published';
}

function lessonContentType(lesson: TeacherLesson): 'video' | 'reading' | 'quiz' {
  const blob = `${lesson.title} ${lesson.description ?? ''}`.toLowerCase();
  if (blob.includes('quiz') || blob.includes('exam')) return 'quiz';
  if (blob.includes('video') || blob.includes('watch')) return 'video';
  let h = 0;
  for (let i = 0; i < lesson.id.length; i += 1) h = (h + lesson.id.charCodeAt(i) * (i + 1)) % 3;
  return h === 0 ? 'video' : h === 1 ? 'reading' : 'quiz';
}

function typeLabel(t: 'video' | 'reading' | 'quiz'): string {
  if (t === 'video') return 'Video lesson';
  if (t === 'quiz') return 'Quiz';
  return 'Reading';
}

function typeDurationLabel(t: 'video' | 'reading' | 'quiz'): string {
  if (t === 'video') return '15–20 min';
  if (t === 'quiz') return '10 min';
  return '20 min read';
}

function engagementForLesson(lesson: TeacherLesson, status: 'published' | 'scheduled' | 'draft'): number | null {
  if (status === 'draft') return null;
  let h = 0;
  for (let i = 0; i < lesson.id.length; i += 1) h = (h * 31 + lesson.id.charCodeAt(i)) % 100;
  return 40 + (h % 55);
}

const moduleIcons = [FaGraduationCap, FaFlask, FaBook];

const ClassroomTeacherLessonsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [selectedClass, setSelectedClass] = useState('all');
  const [dateFilter, setDateFilter] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [draftTitle, setDraftTitle] = useState('');
  const [draftDescription, setDraftDescription] = useState('');
  const [draftDate, setDraftDate] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [newLessonClassId, setNewLessonClassId] = useState('');
  const [previewLessonId, setPreviewLessonId] = useState<string | null>(null);

  const [lessonsQuery, overviewQuery] = useQueries({
    queries: [
      { queryKey: ['classroom', 'teacher', 'lessons'], queryFn: teacherService.getLessons },
      { queryKey: ['classroom', 'teacher', 'overview'], queryFn: teacherService.getOverview },
    ],
  });

  const saveLesson = useMutation({
    mutationFn: teacherService.saveLesson,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['classroom', 'teacher', 'lessons'] });
      setDraftTitle('');
      setDraftDescription('');
      setDraftDate('');
      setEditingId(null);
    },
  });

  const classNameById = useMemo(() => {
    const m = new Map<string, string>();
    (overviewQuery.data ?? []).forEach((c) => m.set(c.id, c.name));
    return m;
  }, [overviewQuery.data]);

  const classesData = overviewQuery.data;

  useEffect(() => {
    const fromQuery = searchParams.get('class')?.trim();
    if (fromQuery && (classesData ?? []).some((c) => c.id === fromQuery)) {
      setSelectedClass(fromQuery);
      setNewLessonClassId(fromQuery);
    }
  }, [searchParams, classesData]);

  useEffect(() => {
    if (newLessonClassId) return;
    const first = classesData?.[0]?.id;
    if (first) setNewLessonClassId(first);
  }, [classesData, newLessonClassId]);

  const classOptions = classesData ?? [];

  const filteredLessons = useMemo(() => {
    let list = [...(lessonsQuery.data ?? [])];
    if (selectedClass !== 'all') list = list.filter((l) => l.class_id === selectedClass);
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (l) =>
          l.title.toLowerCase().includes(q) ||
          (l.description ?? '').toLowerCase().includes(q) ||
          (classNameById.get(l.class_id) ?? '').toLowerCase().includes(q)
      );
    }
    if (dateFilter) list = list.filter((l) => (l.lesson_date ?? '').slice(0, 10) === dateFilter);
    return list;
  }, [lessonsQuery.data, selectedClass, search, dateFilter, classNameById]);

  const stats = useMemo(() => {
    const total = filteredLessons.length;
    const drafts = filteredLessons.filter((l) => lessonStatus(l) === 'draft').length;
    const scheduled = filteredLessons.filter((l) => lessonStatus(l) === 'scheduled').length;
    return { total, drafts, scheduled };
  }, [filteredLessons]);

  const modules = useMemo(() => {
    const byClass = new Map<string, TeacherLesson[]>();
    filteredLessons.forEach((l) => {
      const arr = byClass.get(l.class_id) ?? [];
      arr.push(l);
      byClass.set(l.class_id, arr);
    });
    byClass.forEach((arr) => arr.sort((a, b) => a.sequence - b.sequence || a.title.localeCompare(b.title)));
    const ids = Array.from(byClass.keys()).sort((a, b) => {
      const na = classNameById.get(a) ?? a;
      const nb = classNameById.get(b) ?? b;
      return na.localeCompare(nb);
    });
    return ids.map((classId, index) => ({
      classId,
      className: classNameById.get(classId) ?? 'Course',
      lessons: byClass.get(classId) ?? [],
      moduleIndex: index,
    }));
  }, [filteredLessons, classNameById]);

  const flatOrdered = useMemo(() => {
    const rows: TeacherLesson[] = [];
    modules.forEach((m) => rows.push(...m.lessons));
    return rows;
  }, [modules]);

  const reorder = useCallback(
    async (lessonId: string, direction: -1 | 1) => {
      const idx = flatOrdered.findIndex((l) => l.id === lessonId);
      if (idx < 0) return;
      const current = flatOrdered[idx];
      const target = flatOrdered[idx + direction];
      if (!current || !target) return;
      if (current.class_id !== target.class_id) return;
      await saveLesson.mutateAsync({ ...current, id: current.id, sequence: target.sequence });
      await saveLesson.mutateAsync({ ...target, id: target.id, sequence: current.sequence });
    },
    [flatOrdered, saveLesson]
  );

  const beginEdit = (lesson: TeacherLesson) => {
    setPreviewLessonId(null);
    setEditingId(lesson.id);
    setDraftTitle(lesson.title);
    setDraftDescription(lesson.description ?? '');
    setDraftDate((lesson.lesson_date ?? '').slice(0, 10));
    document.getElementById('lesson-editor')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const clearEditor = () => {
    setEditingId(null);
    setDraftTitle('');
    setDraftDescription('');
    setDraftDate('');
  };

  const submitSave = () => {
    const targetClass = newLessonClassId || classOptions[0]?.id || 'class-1';
    if (editingId) {
      const existing = (lessonsQuery.data ?? []).find((l) => l.id === editingId);
      if (!existing) return;
      saveLesson.mutate({
        id: editingId,
        class_id: existing.class_id,
        title: draftTitle || 'Untitled lesson',
        description: draftDescription || null,
        lesson_date: draftDate || null,
        sequence: existing.sequence,
      });
      return;
    }
    const classLessons = (lessonsQuery.data ?? []).filter((l) => l.class_id === targetClass);
    const maxSeq = classLessons.reduce((m, l) => Math.max(m, l.sequence), 0);
    saveLesson.mutate({
      class_id: targetClass,
      title: draftTitle || 'Untitled lesson',
      description: draftDescription || null,
      lesson_date: draftDate || null,
      sequence: maxSeq + 1,
    });
  };

  const duplicateLesson = async (lesson: TeacherLesson) => {
    const classLessons = (lessonsQuery.data ?? []).filter((l) => l.class_id === lesson.class_id);
    const maxSeq = classLessons.reduce((m, l) => Math.max(m, l.sequence), 0);
    await saveLesson.mutateAsync({
      class_id: lesson.class_id,
      title: `${lesson.title} (copy)`,
      description: lesson.description,
      lesson_date: lesson.lesson_date,
      sequence: maxSeq + 1,
    });
  };

  if (lessonsQuery.isLoading || overviewQuery.isLoading) {
    return <p style={{ color: 'var(--textSecondary, var(--onSurfaceVariant))' }}>Loading lessons…</p>;
  }

  if (lessonsQuery.isError || overviewQuery.isError) {
    return <p style={{ color: 'var(--textSecondary, var(--onSurfaceVariant))' }}>Unable to load lessons.</p>;
  }

  return (
    <Page>
      <PageHeaderRow>
        <TitleBlock>
          <PageTitle>Lessons</PageTitle>
          <PageSubtitle>Manage your curriculum and lesson delivery across all active modules.</PageSubtitle>
        </TitleBlock>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.65rem' }}>
          <StatsRow>
            <StatCard $accent="primary">
              <StatLabel>TOTAL LESSONS</StatLabel>
              <StatValue>{stats.total}</StatValue>
            </StatCard>
            <StatCard $accent="warning">
              <StatLabel>DRAFTS</StatLabel>
              <StatValue>{stats.drafts}</StatValue>
            </StatCard>
            <StatCard $accent="muted">
              <StatLabel>SCHEDULED</StatLabel>
              <StatValue>{stats.scheduled}</StatValue>
            </StatCard>
          </StatsRow>
          <PrimaryBtn
            type="button"
            onClick={() => {
              clearEditor();
              if (selectedClass !== 'all') setNewLessonClassId(selectedClass);
              document.getElementById('lesson-editor')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
          >
            Create new
          </PrimaryBtn>
        </div>
      </PageHeaderRow>

      <div>
        <Toolbar>
          <SearchWrap>
            <FaSearch size={15} aria-hidden />
            <SearchInput
              placeholder="Search lessons or courses…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search lessons"
            />
          </SearchWrap>
          <Select value={selectedClass} onChange={(e) => setSelectedClass(e.target.value)} aria-label="Course filter">
            <option value="all">All courses</option>
            {classOptions.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
          <FilterBtn type="button" onClick={() => setFiltersOpen((o) => !o)}>
            <FaFilter size={14} aria-hidden />
            Filters
          </FilterBtn>
        </Toolbar>
        <FiltersPanel $open={filtersOpen}>
          <FilterField>
            Lesson date
            <input type="date" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} />
          </FilterField>
          {dateFilter ? (
            <GhostBtn type="button" onClick={() => setDateFilter('')}>
              Clear date
            </GhostBtn>
          ) : null}
        </FiltersPanel>
      </div>

      {modules.length === 0 ? (
        <p style={{ color: 'var(--textSecondary, var(--onSurfaceVariant))' }}>
          No lessons match your filters. Try adjusting search or create a new lesson below.
        </p>
      ) : (
        modules.map((mod) => {
          const ModIcon = moduleIcons[mod.moduleIndex % moduleIcons.length] ?? FaBook;
          return (
            <ModuleBlock key={mod.classId}>
              <ModuleHead>
                <ModuleIconBox>
                  <ModIcon size={20} aria-hidden />
                </ModuleIconBox>
                <ModuleTitles>
                  <ModuleTitle>
                    Module {mod.moduleIndex + 1}: {mod.className}
                  </ModuleTitle>
                  <ModuleMeta>
                    Active in {mod.className} · {mod.lessons.length}{' '}
                    {mod.lessons.length === 1 ? 'lesson' : 'lessons'}
                  </ModuleMeta>
                </ModuleTitles>
              </ModuleHead>
              {mod.lessons.map((lesson) => {
                const status = lessonStatus(lesson);
                const ctype = lessonContentType(lesson);
                const eng = engagementForLesson(lesson, status);
                const statusLabel =
                  status === 'published' ? 'PUBLISHED' : status === 'scheduled' ? 'SCHEDULED' : 'DRAFT';
                const previewOpen = previewLessonId === lesson.id;
                return (
                  <React.Fragment key={lesson.id}>
                  <LessonRow
                    style={{
                      borderBottomLeftRadius: previewOpen ? 0 : 12,
                      borderBottomRightRadius: previewOpen ? 0 : 12,
                      marginBottom: previewOpen ? 0 : undefined,
                    }}
                  >
                    <TypeIcon $t={ctype} aria-hidden>
                      {ctype === 'video' ? <FaPlay size={14} /> : ctype === 'quiz' ? <FaQuestion size={14} /> : <FaBook size={14} />}
                    </TypeIcon>
                    <LessonInfo>
                      <LessonTitle>{lesson.title}</LessonTitle>
                      <LessonSub>
                        {typeLabel(ctype)} · {typeDurationLabel(ctype)}
                      </LessonSub>
                    </LessonInfo>
                    <LessonStatusCell>
                      <StatusPill $status={status}>{statusLabel}</StatusPill>
                    </LessonStatusCell>
                    <LessonEngageCell>
                      <EngagementLabel>Engagement</EngagementLabel>
                      {eng == null ? (
                        <EngPct>—</EngPct>
                      ) : (
                        <>
                          <EngBarTrack>
                            <EngBarFill $pct={eng} />
                          </EngBarTrack>
                          <EngPct>{eng}%</EngPct>
                        </>
                      )}
                    </LessonEngageCell>
                    <LessonActionsCell>
                      <IconBtn
                        type="button"
                        title="Move up"
                        onClick={() => void reorder(lesson.id, -1)}
                        disabled={mod.lessons[0]?.id === lesson.id}
                      >
                        <FaChevronUp size={14} />
                      </IconBtn>
                      <IconBtn
                        type="button"
                        title="Move down"
                        onClick={() => void reorder(lesson.id, 1)}
                        disabled={mod.lessons[mod.lessons.length - 1]?.id === lesson.id}
                      >
                        <FaChevronDown size={14} />
                      </IconBtn>
                      <IconBtn type="button" title="Edit" onClick={() => beginEdit(lesson)}>
                        <FaEdit size={14} />
                      </IconBtn>
                      <IconBtn
                        type="button"
                        title={previewOpen ? 'Hide details' : 'View details'}
                        onClick={() => setPreviewLessonId(previewOpen ? null : lesson.id)}
                      >
                        <FaEye size={14} />
                      </IconBtn>
                      <IconBtn type="button" title="Duplicate" onClick={() => void duplicateLesson(lesson)}>
                        <FaClone size={14} />
                      </IconBtn>
                      <IconBtn type="button" title="Delete (not available yet)" disabled>
                        <FaTrashAlt size={14} />
                      </IconBtn>
                    </LessonActionsCell>
                  </LessonRow>
                  {previewOpen ? (
                    <LessonPreviewPanel>
                      {lesson.description?.trim() || 'No description yet. Use Edit to add lesson details.'}
                    </LessonPreviewPanel>
                  ) : null}
                  </React.Fragment>
                );
              })}
            </ModuleBlock>
          );
        })
      )}

      <EditorCard id="lesson-editor">
        <EditorTitle>{editingId ? 'Edit lesson' : 'New lesson'}</EditorTitle>
        <EditorGrid>
          <label>
            Class
            <select
              value={
                editingId
                  ? ((lessonsQuery.data ?? []).find((l) => l.id === editingId)?.class_id ?? newLessonClassId)
                  : newLessonClassId
              }
              onChange={(e) => {
                if (!editingId) setNewLessonClassId(e.target.value);
              }}
              disabled={!!editingId}
            >
              {classOptions.length === 0 ? <option value="">No classes</option> : null}
              {classOptions.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Title
            <input value={draftTitle} onChange={(e) => setDraftTitle(e.target.value)} placeholder="Lesson title" />
          </label>
          <label>
            Date
            <input type="date" value={draftDate} onChange={(e) => setDraftDate(e.target.value)} />
          </label>
          <label>
            Description
            <textarea value={draftDescription} onChange={(e) => setDraftDescription(e.target.value)} rows={4} placeholder="Objectives, resources, notes…" />
          </label>
        </EditorGrid>
        <EditorActions>
          <PrimaryBtn
            type="button"
            onClick={() => void submitSave()}
            disabled={saveLesson.isPending || (!editingId && classOptions.length === 0)}
          >
            {saveLesson.isPending ? 'Saving…' : editingId ? 'Update lesson' : 'Save lesson'}
          </PrimaryBtn>
          {editingId ? (
            <GhostBtn type="button" onClick={clearEditor}>
              Cancel edit
            </GhostBtn>
          ) : null}
        </EditorActions>
      </EditorCard>
    </Page>
  );
};

export default ClassroomTeacherLessonsPage;
